import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Home as HomeIcon, CheckCircle2, MapPin } from "lucide-react";

export default function Homecell() {
  const [f, setF] = useState({ full_name: "", email: "", phone: "", suburb: "", city: "", additional_info: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/homecell", f);
      setDone(true);
      toast.success("A homecell leader will contact you soon.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-32 text-center" data-testid="homecell-success">
        <CheckCircle2 size={42} className="text-terracotta mx-auto" />
        <p className="overline text-terracotta mt-4">See you at homecell</p>
        <h1 className="font-heading font-black text-5xl tracking-tighter text-olive mt-3">You're in, {f.full_name.split(" ")[0]}.</h1>
        <p className="mt-6 text-ink/75 text-lg">
          A homecell leader near <span className="font-semibold">{f.suburb}</span> will reach out to you shortly.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="homecell-page">
      <section className="bg-cream border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <p className="overline text-terracotta mb-4">Get Involved</p>
          <h1 className="font-heading font-black text-5xl sm:text-6xl tracking-tighter text-olive leading-[0.95] max-w-3xl">
            Join a <em className="italic font-light text-terracotta">Homecell.</em>
          </h1>
          <p className="mt-6 text-ink/70 text-lg max-w-2xl">
            Life in community — a small group near you where you can grow, belong, and build
            real friendships.
          </p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
        <div className="grid md:grid-cols-3 gap-4 mb-10">
          {[
            { icon: HomeIcon, title: "Meet weekly", text: "Real conversations around a table." },
            { icon: MapPin, title: "Near your suburb", text: "We'll match you to the closest group." },
            { icon: CheckCircle2, title: "Everyone welcome", text: "No pressure — just show up." },
          ].map((b) => (
            <div key={b.title} className="bg-white border border-border p-5">
              <b.icon size={18} className="text-olive" />
              <p className="font-heading font-bold text-ink mt-3">{b.title}</p>
              <p className="text-sm text-muted_ink mt-1">{b.text}</p>
            </div>
          ))}
        </div>

        <form onSubmit={submit} className="bg-white border border-border p-8 md:p-10 space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Full name *</Label>
              <Input required value={f.full_name} onChange={set("full_name")} data-testid="hc-name" className="mt-2 h-11" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" required value={f.email} onChange={set("email")} data-testid="hc-email" className="mt-2 h-11" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Phone *</Label>
              <Input required value={f.phone} onChange={set("phone")} data-testid="hc-phone" className="mt-2 h-11" />
            </div>
            <div>
              <Label>City / Town</Label>
              <Input value={f.city} onChange={set("city")} placeholder="e.g. Mbombela" data-testid="hc-city" className="mt-2 h-11" />
            </div>
          </div>
          <div>
            <Label>Suburb / Area *</Label>
            <Input required value={f.suburb} onChange={set("suburb")} placeholder="e.g. Kanyamazane-A" data-testid="hc-suburb" className="mt-2 h-11" />
          </div>
          <div>
            <Label>Anything a leader should know? (optional)</Label>
            <Textarea rows={3} value={f.additional_info} onChange={set("additional_info")} data-testid="hc-info" className="mt-2" />
          </div>
          <Button type="submit" disabled={loading} data-testid="hc-submit"
            className="bg-terracotta hover:bg-terracotta-hover text-white rounded-none h-12 px-10 text-base font-semibold">
            {loading ? "Submitting…" : "Sign me up"}
          </Button>
        </form>
      </section>
    </div>
  );
}
